import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useBackRoute } from '../hooks/useBackRoute';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import { fetchHakedisler, fetchHakedisDetay, hakedisFaturaGir, HAKEDIS_FATURA_BEKLEYEN_SAYAC_YENILE_EVENT } from '../services/hakedisService';
import HakedisDetayModal from '../components/HakedisDetayModal';
import HakedisFaturaModal from '../components/HakedisFaturaModal';
import TableListFooter from '../components/TableListFooter';
import { PAGE_DEFAULT_LIMIT } from '../constants/pagination';
import { normalizeSayfalama } from '../utils/pagination';

const TABS = [
  { id: 'fatura-bekleyen', label: 'Fatura Bekleyen Hakedişler', tab: 'fatura-bekleyen' },
  { id: 'tamamlanan', label: 'Tamamlanan Hakedişler', tab: 'tamamlanan' },
];

const DURUM_LABELS = {
  FaturaBekleyen: 'Fatura Bekleyen',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const getHakedisId = (h) => toValidObjectIdString(h?.id ?? h?._id) || '';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const TechServiceHakedislerPage = () => {
  const goBack = useBackRoute('/');
  const [hakedisler, setHakedisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('fatura-bekleyen');
  const [selectedHakedis, setSelectedHakedis] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [faturaModalOpen, setFaturaModalOpen] = useState(false);
  const [faturaModalHakedis, setFaturaModalHakedis] = useState(null);
  const [faturaSubmitting, setFaturaSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });

  const tabConfig = useMemo(() => TABS.find((t) => t.id === activeTab) || TABS[0], [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const loadHakedisler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { sayfa: currentPage, limit: PAGE_DEFAULT_LIMIT, tab: tabConfig.tab };
      const response = await fetchHakedisler(params);
      if (response?.basarili && response?.veri) {
        setHakedisler(response.veri.hakedisler || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      } else {
        setHakedisler([]);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Hakedişler yüklenemedi'));
      setHakedisler([]);
    } finally {
      setLoading(false);
    }
  }, [tabConfig, currentPage]);

  useEffect(() => {
    loadHakedisler();
  }, [loadHakedisler]);

  const handleViewDetail = async (hakedis) => {
    const id = getHakedisId(hakedis);
    if (!id) {
      window.showNotification?.('error', 'Hakediş ID alınamadı');
      return;
    }
    try {
      const response = await fetchHakedisDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedHakedis(response.veri.hakedis);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  const handleOpenFaturaModal = (hakedis) => {
    setFaturaModalHakedis(hakedis);
    setFaturaModalOpen(true);
  };

  const handleFaturaSubmit = async (faturaNumarasi) => {
    const id = getHakedisId(faturaModalHakedis);
    if (!id) return;
    setFaturaSubmitting(true);
    try {
      const response = await hakedisFaturaGir(id, faturaNumarasi);
      if (response?.basarili) {
        window.showNotification?.('success', 'Fatura bilgisi eklendi');
        setFaturaModalOpen(false);
        setFaturaModalHakedis(null);
        loadHakedisler();
        window.dispatchEvent(new Event(HAKEDIS_FATURA_BEKLEYEN_SAYAC_YENILE_EVENT));
      } else {
        window.showNotification?.('error', response?.mesaj || 'Fatura bilgisi eklenemedi');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Fatura bilgisi eklenemedi'));
    } finally {
      setFaturaSubmitting(false);
    }
  };

  return (
    <main className="main-content tech-service-hakedisler">
      <div className="content-header">
        <div>
          <button
            type="button"
            className="secondary-btn"
            onClick={goBack}
            style={{ marginBottom: '16px' }}
          >
            <i className="fas fa-arrow-left"></i>
            <span>Geri Dön</span>
          </button>
          <h1>Hakedişler</h1>
        </div>
      </div>

      <div className="tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.id);
              setCurrentPage(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Hakedişler yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : hakedisler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-file-invoice-dollar" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Hakediş bulunamadı</div>
          </div>
        ) : (
          <>
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Randevu No</th>
                  <th>Tarih</th>
                  <th style={{ textAlign: 'right' }}>Tutar</th>
                  <th>Durum</th>
                  <th className="islemler-sutunu">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {hakedisler.map((h, idx) => (
                  <tr key={getHakedisId(h) || idx}>
                    <td style={{ fontWeight: 500 }}>{h.randevuNumarasi || '—'}</td>
                    <td>{formatApiDate(h.olusturmaTarihi)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(h.toplamTutar)}</td>
                    <td>{DURUM_LABELS[h.durum] || h.durum}</td>
                    <td className="table-actions islemler-sutunu" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => handleViewDetail(h)}
                        title="Detay"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      {activeTab === 'fatura-bekleyen' && h.durum === 'FaturaBekleyen' && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{ marginLeft: 8, padding: '4px 10px', fontSize: 12 }}
                          onClick={() => handleOpenFaturaModal(h)}
                          title="Fatura Bilgisi Ekle"
                        >
                          <i className="fas fa-file-invoice"></i> Fatura Ekle
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TableListFooter
            totalRecords={paginationMeta.total}
            currentPage={currentPage}
            totalPages={paginationMeta.totalPages || 1}
            onPageChange={setCurrentPage}
            loading={loading}
            embedded
          />
          </>
        )}
      </div>

      {detailModalOpen && selectedHakedis && (
        <HakedisDetayModal
          hakedis={selectedHakedis}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedHakedis(null);
          }}
          onRefresh={loadHakedisler}
        />
      )}

      {faturaModalOpen && faturaModalHakedis && (
        <HakedisFaturaModal
          hakedis={faturaModalHakedis}
          onClose={() => {
            setFaturaModalOpen(false);
            setFaturaModalHakedis(null);
          }}
          onSubmit={handleFaturaSubmit}
          isSubmitting={faturaSubmitting}
        />
      )}
    </main>
  );
};

export default TechServiceHakedislerPage;
